/**
 * Library sharing — the wire shapes of "Shared with me".
 *
 * ONE SCHEMA, BOTH ENDS (LB6-T4-R3). `GET /library/acl/shared-with-me`
 * validates its response with `SharedWithMeResponseSchema` and the Shared page
 * types its rows from the same schema, so a new `via` kind or a new granter
 * field is a compile error on the page, not a blank cell. (The page's
 * hand-written mirror had no `doc_department` / `folder_department` while the
 * server already sent them, and the Via cell rendered nothing.)
 */

import { z } from 'zod';
import { NamedUserSchema } from './user-display.js';

/**
 * The permissions a share may carry, weakest first. Mirrors the DB CHECK on
 * `document_acl_grants.permission` / `folder_acl_grants.permission`;
 * packages/api/src/db/schema/document-acl.ts re-exports this list.
 */
export const ACL_PERMISSIONS = ['view', 'edit', 'delete', 'share'] as const;
export type AclPermission = (typeof ACL_PERMISSIONS)[number];

/** How a Shared-with-me document reached this person: its winning share. */
export const SHARED_VIA = [
  'doc_user',
  'doc_group',
  'doc_role',
  'doc_department',
  'folder_user',
  'folder_group',
  'folder_role',
  'folder_department',
] as const;
export type SharedVia = (typeof SHARED_VIA)[number];

/**
 * The longest title / file-name search the list accepts. The search box's
 * `maxLength` and the route's query schema both read it, so a pasted long
 * name is cut at the box, never answered with a 400 (LB6-T4-BF-03).
 */
export const SHARED_WITH_ME_SEARCH_MAX = 200;

/** One Shared-with-me document, as the server answers it. */
export const SharedWithMeRowSchema = z.object({
  documentId: z.string().uuid(),
  title: z.string(),
  fileName: z.string(),
  permission: z.enum(ACL_PERMISSIONS),
  grantedAt: z.string(),
  grantedBy: z.string().uuid().nullable(),
  /**
   * WHO SHARED IT (decision 30, B9), named by the server. `null` with
   * `grantedBy` null is a system grant. `null` with `grantedBy` set is an
   * account this tenant has never had as a member — the server does not name
   * a person outside the tenant (LB6-T4-R2).
   */
  granter: NamedUserSchema.nullable(),
  expiresAt: z.string().nullable(),
  via: z.enum(SHARED_VIA),
  reason: z.string().nullable(),
});
export type SharedWithMeRow = z.infer<typeof SharedWithMeRowSchema>;

export const SharedWithMeResponseSchema = z.object({
  rows: z.array(SharedWithMeRowSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  pageSize: z.number().int().positive(),
});
export type SharedWithMeResponse = z.infer<typeof SharedWithMeResponseSchema>;
